import { matchPath } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import NavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import { FilterKeys } from '@common/enums/query-params/filters';
import { TAB_QUERY_STRING } from 'utils/url-tabs-helper';
import { AppPlatform, PageName } from './analytics-events';

/*
 * Pure helpers that turn app state into analytics-safe values.
 *
 * Privacy rule for everything in this file: nothing that is typed by a user, and nothing that
 * identifies a person (names, email addresses, phone numbers, free text), may leave these helpers.
 * Paths are reduced to route templates, query strings are dropped except for enum-valued tabs,
 * and error payloads are reduced to localization keys.
 */

const ORG = `${NavigationRoutes.Dashboard}:orgId`;

// Order matters: the first pattern that matches wins, mirroring the <Routes> in App.tsx.
const LOGGED_IN_PAGES: { pattern: string; name: PageName }[] = [
  { pattern: NavigationRoutes.InsightsDatasets, name: PageName.InsightsDatasets },
  { pattern: NavigationRoutes.Insights, name: PageName.Insights },
  { pattern: NavigationRoutes.Register, name: PageName.Register },
  { pattern: NavigationRoutes.Dashboard, name: PageName.Home },
  { pattern: `${ORG}${NavigationRoutes.Statistics}`, name: PageName.Statistics },
  { pattern: `${ORG}${NavigationRoutes.MissionHistory}`, name: PageName.MissionHistory },
  { pattern: `${ORG}${NavigationRoutes.Users}`, name: PageName.Users },
  { pattern: `${ORG}${NavigationRoutes.Organizations}`, name: PageName.Organizations },
  { pattern: `${ORG}${NavigationRoutes.Settings}`, name: PageName.OrganizationSettings },
  { pattern: `${ORG}${NavigationRoutes.Administration}`, name: PageName.Administration },
  { pattern: `${ORG}${NavigationRoutes.Legal}`, name: PageName.Legal },
  { pattern: `${ORG}${NavigationRoutes.AccountData}`, name: PageName.AccountData },
  { pattern: `${ORG}${NavigationRoutes.Unsubscribe}`, name: PageName.Unsubscribe },
  { pattern: ORG, name: PageName.OrganizationDashboard },
];

const LOGGED_OUT_PAGES: { pattern: string; name: PageName }[] = [
  { pattern: NavigationRoutes.Legal, name: PageName.Legal },
  { pattern: NavigationRoutes.AccountData, name: PageName.AccountData },
  { pattern: NavigationRoutes.Register, name: PageName.Register },
  { pattern: NavigationRoutes.Unsubscribe, name: PageName.Unsubscribe },
];

export interface PageInfo {
  pageName: PageName;
  /** Route template, e.g. `/:orgId/statistics` — never the concrete URL. */
  pagePath: string;
}

export const resolvePage = (pathname: string, isLoggedIn: boolean): PageInfo => {
  const pages = isLoggedIn ? LOGGED_IN_PAGES : LOGGED_OUT_PAGES;
  for (const page of pages) {
    if (matchPath({ path: page.pattern, end: true }, pathname) != null) {
      return { pageName: page.name, pagePath: page.pattern };
    }
  }
  return isLoggedIn
    ? { pageName: PageName.NotFound, pagePath: '*' }
    : { pageName: PageName.Login, pagePath: '*' };
};

// Tab values are enum strings chosen by the app; anything else is dropped.
const SAFE_TOKEN = /^[a-z0-9_-]{1,40}$/i;

export const getPageTab = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const tab = params.get(TAB_QUERY_STRING) ?? params.get(FilterKeys.Tab);
  return tab != null && SAFE_TOKEN.test(tab) ? tab : null;
};

/** Which detail panel is open on top of the page, from presence of the id — never the id itself. */
export const getDetailView = (search: string): string | null => {
  const params = new URLSearchParams(search);
  if (params.has(FilterKeys.RobotId)) return 'robot';
  if (params.has(FilterKeys.MissionId)) return 'mission';
  if (params.has(FilterKeys.MissionHistoryId)) return 'mission_history';
  if (params.has(FilterKeys.UserId)) return 'user';
  return null;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
// Serial numbers, tokens and other opaque identifiers: long, mixed letters and digits.
const OPAQUE = /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{8,}$/i;

/**
 * Reduces a request URL to an endpoint template: `/api/v1.0/robots/<uuid>/missions/?page=2`
 * becomes `robots/:id/missions`. Identifiers and query strings are removed.
 */
export const toEndpointTemplate = (url: string | undefined): string => {
  if (url == null || url.length === 0) return 'unknown';
  const withoutQuery = url.split(/[?#]/)[0];
  const withoutHost = withoutQuery.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const segments = withoutHost.split('/').filter((s) => s.length > 0);
  if (segments[0] === 'api') segments.shift();
  if (segments[0] != null && /^v\d/.test(segments[0])) segments.shift();
  const template = segments.map((s) =>
    UUID.test(s) || NUMERIC.test(s) || OPAQUE.test(s) || s.includes('@') ? ':id' : s,
  );
  return template.length > 0 ? template.join('/') : 'unknown';
};

// Backend validators return localization enum keys, e.g. "error-robot-not-found".
const LOCALIZATION_KEY = /^[a-z][a-z0-9]*(-[a-z0-9]+)+$/;
const MAX_ERROR_KEYS = 5;

/**
 * Pulls localization error keys out of an error response body. Anything that is not shaped like a
 * key (free text, echoed input values, HTML error pages) is dropped, so user data cannot leak.
 */
export const extractErrorKeys = (data: unknown): string[] => {
  const keys = new Set<string>();
  const visit = (value: unknown, depth: number) => {
    if (keys.size >= MAX_ERROR_KEYS || depth > 3) return;
    if (typeof value === 'string') {
      if (LOCALIZATION_KEY.test(value) && value.length <= 80) keys.add(value);
    } else if (Array.isArray(value)) {
      value.forEach((v) => visit(v, depth + 1));
    } else if (value != null && typeof value === 'object') {
      Object.values(value).forEach((v) => visit(v, depth + 1));
    }
  };
  visit(data, 0);
  return Array.from(keys);
};

export const getErrorCategory = (status: number | undefined): string => {
  if (status == null || status === 0) return 'network';
  if (status === 401 || status === 403) return 'auth';
  if (status === 404) return 'not_found';
  if (status === 400 || status === 409 || status === 422) return 'validation';
  if (status === 423 || status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'client';
};

export const getAppPlatform = (): AppPlatform => {
  if (Capacitor.isNativePlatform()) return AppPlatform.Native;
  if (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) {
    return AppPlatform.Pwa;
  }
  return AppPlatform.Browser;
};

/** Coarse size band, so the exact fleet size of an organisation is not exported. */
export const toRobotCountBand = (count: number | null | undefined): string => {
  if (count == null || count <= 0) return '0';
  if (count <= 5) return '1-5';
  if (count <= 20) return '6-20';
  if (count <= 100) return '21-100';
  return '100+';
};

/** `2026-07-31T10:00:00Z` → `2026-07`. Month granularity is enough for cohorting. */
export const toMonth = (isoDate: string | null | undefined): string | null => {
  if (isoDate == null) return null;
  const match = /^(\d{4})-(\d{2})/.exec(isoDate);
  return match ? `${match[1]}-${match[2]}` : null;
};
